import { telemetry } from "../telemetry/telemetry";

const TTL_MS = 5 * 1000;
const MAX_ENTRIES = 500;

const seen = new Map<string, number>();

function prune(now: number): void {
  if (seen.size <= MAX_ENTRIES) {
    for (const [id, ts] of seen) {
      if (now - ts > TTL_MS) seen.delete(id);
    }
    return;
  }
  // Hard cap exceeded — drop oldest entries.
  const entries = Array.from(seen.entries()).sort((a, b) => a[1] - b[1]);
  const toRemove = entries.length - MAX_ENTRIES;
  for (let i = 0; i < toRemove; i++) seen.delete(entries[i][0]);
}

export type CallSource = "socket" | "rehydrate";

export function shouldPlay(callId: string, source: CallSource): boolean {
  if (!callId) return source === "socket";
  const now = Date.now();
  prune(now);

  if (seen.has(callId)) {
    if (source === "socket") {
      telemetry("dedup_block", { callId, source });
    }
    return false;
  }

  seen.set(callId, now);
  if (source === "rehydrate") {
    return false;
  }
  return true;
}

export function markSeen(callId: string): void {
  if (!callId) return;
  seen.set(callId, Date.now());
}

export function _resetForTests(): void {
  seen.clear();
}

export function callIdForPassword(password: any): string {
  const id = password?.idsenhaatendimento ?? "unknown";
  const stamp = password?.datachamada ?? password?.dataatualizacao ?? "no-ts";
  return `pwd:${id}:${stamp}`;
}

export function callIdForOffice(payload: {
  idpacienteconsultorio?: string;
  panels?: Array<{ idconsultorio?: string; idpainelconsultorio?: string }>;
  nomepaciente?: string;
}): string {
  // 1. Id estavel direto, se o backend mandar (preferido).
  if (payload?.idpacienteconsultorio) {
    return `off:stable:${payload.idpacienteconsultorio}`;
  }
  // 2. idpainelconsultorio do primeiro panel — estavel por linha do DB.
  //    Existe no socket payload (ICallPanelOffice).
  const stable = payload?.panels?.[0]?.idpainelconsultorio;
  if (stable) {
    return `off:panel:${stable}`;
  }
  // 3. Fallback: consultorio+paciente em janela de 10s.
  //    Bloqueia reentrega rapida do socket sem bloquear rechamada legitima.
  const consultorio = payload?.panels?.[0]?.idconsultorio ?? "unknown";
  const paciente = (payload?.nomepaciente ?? "noname").trim().toLowerCase();
  const window10s = Math.floor(Date.now() / 10000);
  return `off:${consultorio}:${paciente}:${window10s}`;
}
